<template>
  <nav
    data-test="search-results-pagination-controls"
    :class="{ desktop: $isDesktop }"
  >
    <div v-if="isFirstOfMany" class="pagination-controls view-more">
      <pu-search-link
        v-bind="getSearchUrlProps(2)"
        class="button primary asblock"
        @click="$emit('link-clicked')"
      >
        <translate>View more</translate>
      </pu-search-link>
    </div>
    <template v-else>
      <translate
        v-if="!$isDesktop"
        class="summary"
        tag="div"
        :tt="{ currentPage, totalPages }"
      >
        Page {currentPage} of {totalPages}
      </translate>

      <div class="pagination-controls">
        <pu-search-link
          v-if="!isFirst"
          v-bind="getSearchUrlProps(1, 'searchDefault')"
          class="button minimum sml first"
          @click="$emit('link-clicked')"
        >
          <span>
            <img
              loading="lazy"
              class="icon"
              src="@/assets/images/forward-rounded-blue.svg"
              alt=""
              width="8"
              height="15" />
            <img
              loading="lazy"
              class="icon"
              src="@/assets/images/forward-rounded-blue.svg"
              alt=""
              width="8"
              height="15"
          /></span>
          <translate>First</translate>
        </pu-search-link>

        <pu-search-link
          v-if="!isFirst"
          v-bind="
            getSearchUrlProps(
              currentPage - 1,
              currentPage === 2 ? 'searchDefault' : undefined,
            )
          "
          class="button minimum sml prev"
          @click="$emit('link-clicked')"
        >
          <img
            loading="lazy"
            class="icon"
            src="@/assets/images/forward-rounded-blue.svg"
            alt=""
            width="8"
            height="15"
          />
          <translate>Previous</translate>
        </pu-search-link>

        <translate
          v-if="$isDesktop"
          class="summary"
          tag="div"
          :tt="{ currentPage, totalPages }"
        >
          Page {currentPage} of {totalPages}
        </translate>

        <pu-search-link
          v-if="!isLast"
          v-bind="getSearchUrlProps(currentPage + 1)"
          class="button secondary sml next"
          @click="$emit('link-clicked')"
        >
          <img
            loading="lazy"
            class="icon"
            src="@/assets/images/forward-rounded-blue.svg"
            alt=""
            width="8"
            height="15"
          />
          <translate>Next</translate>
        </pu-search-link>
      </div>
    </template>
  </nav>
</template>
<script lang="ts">
import { getCampsiteCategoriesPresenter } from '~/apps/categories/presenter-json/CampsiteCategoriesUpdaterJson'
import { PaginationData, validatePaginationData } from '~/utility/pagination'
import {
  Component,
  Prop,
  Setup,
  Vue,
  toNative,
} from '~/utility/pu-class-decorator'
import { reportError } from '~/utility/reportError'
import type { SearchFilters } from '../schemas'

@Component
class SearchResultsPaginationControls extends Vue {
  @Prop()
  searchFilters: SearchFilters

  @Prop({
    required: true,
    validator: (v) => {
      try {
        validatePaginationData(v)
        return true
      } catch (err) {
        reportError(err as Error)
        return false
      }
    },
  })
  paginationData: PaginationData

  @Prop({ default: false }) useCurrentRoute!: boolean

  @Setup(() => {
    const route = useRoute()
    const presenter = getCampsiteCategoriesPresenter(
      (route.params.lang as string) || 'en-gb',
    )
    return presenter.list.map((item) => item.rootSlug)
  })
  categories: string[]

  get currentPage() {
    return this.paginationData.currentPage
  }

  get totalPages() {
    return this.paginationData.totalPages
  }

  get isFirst() {
    return this.currentPage === 1
  }

  get isLast() {
    return this.currentPage === this.totalPages
  }

  get isFirstOfMany() {
    return this.isFirst && this.totalPages > 1
  }

  getSearchUrlProps(page: number, excludeType?: string) {
    if (this.useCurrentRoute) {
      // If there's a ?facet= query parameter on glamping, switch to /search/
      // SEO URLs like /-/hot-tub/ should stay on glamping route
      const hasQueryFacet = !!this.$route.query.facet
      const isGlamping = this.$route.meta?.type === 'glamping'

      if (hasQueryFacet && isGlamping) {
        // Switch to /search/ with category IDs
        return {
          category: 'search',
          excludeType: excludeType || 'paginationControl',
          modifyFilters: {
            page,
            categoryIds: this.searchFilters.categoryIds,
          },
          moveSingleFacetIntoUrl: false,
        }
      }

      // Keep current route for SEO URLs
      return {
        name: this.$route.name,
        params: { ...this.$route.params },
        query: {
          ...this.$route.query,
          ...(page === 1 ? { page: undefined } : { page }),
        },
        category: 'search',
      }
    }
    let category: string | undefined =
      this.$route.meta?.type === 'campsites' &&
      this.$route.meta?.isSeo !== false
        ? 'campsites'
        : 'search'
    let facet: string | undefined
    const modifyFilters: SearchFilters = { page }
    const isCategorySlugUrl = [
      ...this.categories,
      'glamping',
      'category',
    ].includes(this.$route.meta?.type)

    if (isCategorySlugUrl) {
      category = this.$route.meta!.category
      modifyFilters.categoryIds = undefined
    }
    if (this.isFaceted) {
      category = 'search'
      modifyFilters.categoryIds = this.searchFilters.categoryIds
    }
    return {
      moveSingleFacetIntoUrl: false,
      category,
      excludeType: excludeType || 'paginationControl',
      modifyFilters,
      facet,
    }
  }

  private get isFaceted() {
    return (
      !!this.$route.query.facet ||
      (this.searchFilters.facets?.length === 1 &&
        this.$route.path.includes(`/-/${this.searchFilters.facets[0]}/`))
    )
  }
}

export default toNative(SearchResultsPaginationControls)
</script>

<style scoped>
.pagination-controls {
  display: flex;
  justify-content: space-between;
  width: 100%;
  padding: 0.8em 4% 2em;
  gap: 1em;
}

.summary {
  font-size: var(--body);
  color: var(--colour-dark-blue);
  text-decoration: none;
  text-align: center;
}

.view-more {
  width: 100%;
  white-space: nowrap;
}

.button {
  box-shadow: none;
  border: none;
  padding: 0.75em 1em;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: center;
  row-gap: 0.25em;
  column-gap: 0.5em;
}

.button.next {
  flex-direction: row-reverse;
}

.button.first:active,
.button.prev:active {
  background: #004c80;
  color: white;
}

.icon {
  width: auto;
  height: 1em;
  margin: -0.1em 0 0 0;
}

.next img {
  filter: grayscale(1) brightness(200%);
}

.prev .icon {
  transform: rotate(180deg);
}

.first .icon {
  transform: rotate(180deg);
}

.first .icon:last-of-type {
  margin: -0.1em 0 0 -5px;
}

.first,
.prev {
  padding-left: 0.75em;
}

.prev {
  margin-right: auto;
}

/* desktop & tablet */
.desktop {
  margin: 2em 0;
}

.desktop .pagination-controls {
  padding: 0.8em 0 2em;
}

.desktop .prev {
  margin-right: 0;
}

.desktop .summary {
  margin: auto;
}

.desktop .view-more .button {
  width: auto;
  margin: auto;
  min-width: 16em;
}

.desktop .button {
  font-size: inherit;
}

@media (hover: hover) {
  .button.first:hover,
  .button.prev:hover {
    background: #004c80;
    color: white;
  }
}
</style>
